import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { X, Info, AlertTriangle, CheckCircle, Megaphone } from 'lucide-react';
import { Button } from './ui/button';
import { apiRequest } from '../lib/utils';

const AnnouncementPopup = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [announcements, setAnnouncements] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Don't show on login/register pages
  const hiddenPaths = ['/login', '/register', '/checkout', '/admin'];
  const shouldHide = hiddenPaths.some(p => location.pathname.startsWith(p));

  // Helper to get translated field
  const getTranslated = (item, field) => {
    const lang = i18n.language;
    if (lang === 'en' && item[`${field}_en`]) return item[`${field}_en`];
    if (lang === 'it' && item[`${field}_it`]) return item[`${field}_it`];
    if (lang === 'de' && item[`${field}_de`]) return item[`${field}_de`];
    if (lang === 'fr' && item[`${field}_fr`]) return item[`${field}_fr`];
    if (lang === 'ar' && item[`${field}_ar`]) return item[`${field}_ar`];
    return item[field];
  };

  useEffect(() => {
    if (shouldHide) return;
    const fetchAnnouncements = async () => {
      try {
        const data = await apiRequest('/announcements');
        if (data && data.length > 0) {
          // Check if user has already dismissed these announcements
          const dismissedIds = JSON.parse(localStorage.getItem('dismissedAnnouncements') || '[]');
          const activeIds = data.map(a => a.id);
          
          // Show popup if there are new announcements
          const hasNewAnnouncements = activeIds.some(id => !dismissedIds.includes(id));
          
          if (hasNewAnnouncements) {
            setAnnouncements(data);
            setTimeout(() => setIsVisible(true), 500);
          }
        }
      } catch (error) {
        console.error('Error fetching announcements:', error);
      }
    };

    fetchAnnouncements();
  }, [shouldHide]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      // Save dismissed announcement IDs
      const dismissedIds = announcements.map(a => a.id);
      localStorage.setItem('dismissedAnnouncements', JSON.stringify(dismissedIds));
    }, 300);
  };

  if (!isVisible || announcements.length === 0) return null;

  const typeStyles = {
    info: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', icon: Info },
    success: { bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-400', icon: CheckCircle },
    warning: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400', icon: AlertTriangle },
    promo: { bg: 'bg-[#FFC107]/10', border: 'border-[#FFC107]/30', text: 'text-[#FFC107]', icon: Megaphone }
  };

  return (
    <div 
      className={`fixed inset-0 z-[90] flex items-center justify-center transition-all duration-300 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
      data-testid="announcement-popup"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Popup Content */}
      <div 
        className={`relative w-[95%] max-w-lg bg-[#141417] border border-zinc-700 rounded-2xl overflow-hidden shadow-2xl transform transition-all duration-300 ${
          isVisible && !isClosing ? 'scale-100' : 'scale-95'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#27272A]">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-[#FFC107]" />
            {t('announcements.title')}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-[#25252E] rounded-full transition-colors"
            data-testid="announcement-close-btn"
          >
            <X className="h-5 w-5 text-zinc-400" />
          </button>
        </div>

        {/* Announcements List */}
        <div className="p-4 max-h-[60vh] overflow-y-auto space-y-3">
          {announcements.map((announcement) => {
            const style = typeStyles[announcement.type] || typeStyles.info;
            const IconComponent = style.icon;
            
            return (
              <div 
                key={announcement.id}
                className={`rounded-lg border overflow-hidden ${style.bg} ${style.border}`}
                data-testid={`announcement-item-${announcement.id}`}
              >
                {announcement.image && (
                  <img 
                    src={announcement.image.startsWith('/api') ? announcement.image.startsWith('/api') ? announcement.image : announcement.image : announcement.image}
                    alt={announcement.title}
                    className="w-full h-48 object-cover"
                    data-testid={`announcement-image-${announcement.id}`}
                  />
                )}
                <div className="flex items-start gap-3 p-4">
                  <IconComponent className={`h-5 w-5 flex-shrink-0 mt-0.5 ${style.text}`} />
                  <div className="flex-1">
                    <h3 className={`font-semibold ${style.text}`}>{getTranslated(announcement, 'title')}</h3>
                    <p className="text-zinc-300 text-sm mt-1">{getTranslated(announcement, 'message')}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[#27272A]">
          <Button 
            className="w-full h-12 bg-[#FFC107] text-black hover:bg-[#F59E0B] font-bold uppercase tracking-wider"
            onClick={handleClose}
            data-testid="announcement-ok-btn"
          >
            {t('announcements.gotIt')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementPopup;
